import React, { useState } from 'react';
import {
  Card,
  Input,
  InputNumber,
  Button,
  Space,
  Typography,
  message,
  Divider,
  Radio,
  Tag,
  Spin,
  AutoComplete,
  Alert,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  DUBBO_QUERY_PROVIDERS,
  REFLECT_INVOKE,
  DubboProviderInfo,
} from './graphql/reflectInvoke';

interface Arg {
  type: string;
  value: string;
}

// 统一反射接口（各服务实现同一接口、仅 Dubbo group 不同）：Nacos 按此接口发现全部可调服务实例
const REFLECT_INTERFACE = 'ltd.tita.apiserver.api.dubbo.GenericInvokeDubboService';
const DEFAULT_PORT = 20880;

// 常用参数类型自动补全（含 SchedulerX JobContext，用于触发定时任务）
const TYPE_OPTIONS = [
  'com.alibaba.schedulerx.worker.domain.JobContext',
  'java.lang.String',
  'java.lang.Long',
  'java.lang.Integer',
  'java.lang.Boolean',
  'java.time.Instant',
  'long',
  'int',
  'boolean',
].map((v) => ({ value: v }));

const ReflectInvokePage: React.FC = () => {
  const [host, setHost] = useState('');
  const [port, setPort] = useState<number>(DEFAULT_PORT);
  const [selectedProviderKey, setSelectedProviderKey] = useState<string | undefined>(undefined);
  const [className, setClassName] = useState('');
  const [methodName, setMethodName] = useState('');
  const [args, setArgs] = useState<Arg[]>([{ type: '', value: '' }]);
  const [result, setResult] = useState<string | null>(null);

  const [queryProviders, { data: providersData, loading: queryLoading }] = useLazyQuery<{
    dubboQueryProviders: DubboProviderInfo[];
  }>(DUBBO_QUERY_PROVIDERS, { fetchPolicy: 'no-cache' });

  const [invoke, { loading: invokeLoading }] = useMutation(REFLECT_INVOKE);

  const providers: DubboProviderInfo[] = providersData?.dubboQueryProviders ?? [];

  const handleSearch = () => {
    setSelectedProviderKey(undefined);
    setResult(null);
    queryProviders({ variables: { interfaceName: REFLECT_INTERFACE } });
  };

  const handleSelectProvider = (p: DubboProviderInfo) => {
    setSelectedProviderKey(`${p.host}:${p.port}:${p.version}:${p.group}`);
    setHost(p.host);
    setPort(p.port);
  };

  const addArg = () => setArgs((prev) => [...prev, { type: '', value: '' }]);
  const removeArg = (index: number) => setArgs((prev) => prev.filter((_, i) => i !== index));
  const updateArg = (index: number, field: keyof Arg, value: string) =>
    setArgs((prev) => prev.map((a, i) => (i === index ? { ...a, [field]: value } : a)));

  const fillJobTemplate = () => {
    setClassName('swiftx.delivery.job.PenaltySettlementWeeklyJob');
    setMethodName('process');
    setArgs([{ type: 'com.alibaba.schedulerx.worker.domain.JobContext', value: '' }]);
    message.info('已填入扣罚周任务模板；JobContext 参数值留空=按上周默认，或填 {"startEpochMs":..,"endEpochMs":..}');
  };

  const handleInvoke = async () => {
    if (!host.trim()) { message.warning('请先从 Nacos 选择或手动填写 Host'); return; }
    if (!className.trim()) { message.warning('请输入类路径 className'); return; }
    if (!methodName.trim()) { message.warning('请输入方法名 methodName'); return; }

    const noArgs = args.length === 1 && !args[0].type && !args[0].value;
    const hasEmptyType = args.some((a) => !a.type.trim());
    if (!noArgs && hasEmptyType) {
      message.warning('参数类型不能为空，如无参数请删除参数行');
      return;
    }

    // valueJson 允许为空：JobContext 空值=空 jobParameters；其余类型空值按 null 传
    const outArgs = noArgs
      ? []
      : args.map((a) => ({ type: a.type.trim(), valueJson: a.value === '' ? null : a.value }));

    try {
      const { data } = await invoke({
        variables: {
          host: host.trim(),
          port,
          className: className.trim(),
          methodName: methodName.trim(),
          args: outArgs,
        },
      });
      const raw = data?.reflectInvoke ?? '';
      try { setResult(JSON.stringify(JSON.parse(raw), null, 2)); }
      catch { setResult(raw); }
    } catch (e: any) {
      const errMsg = e?.graphQLErrors?.[0]?.message ?? e?.message ?? '调用失败';
      setResult(`ERROR: ${errMsg}`);
      message.error('调用失败，见结果区');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Typography.Title level={4}>定时任务调用工具</Typography.Title>

      <Alert
        type="warning"
        showIcon
        message="研发调试专用：反射调用可执行目标服务任意 bean 方法，请谨慎使用。需在配置中心开启 dubbo.generic.invoke.enabled。"
      />

      <Card title="① 选择目标服务实例" size="small">
        <Space direction="vertical" className="w-full">
          <Space.Compact style={{ width: '100%' }}>
            <Input value={REFLECT_INTERFACE} disabled />
            <Button type="primary" icon={<SearchOutlined />} loading={queryLoading} onClick={handleSearch}>
              从 Nacos 搜索实例
            </Button>
          </Space.Compact>

          {queryLoading && <Spin />}

          {!queryLoading && providers.length > 0 && (
            <div>
              <div className="text-gray-500 text-xs mb-2">共 {providers.length} 个健康实例，点击自动填入连接信息：</div>
              <Radio.Group
                value={selectedProviderKey}
                onChange={(e) => {
                  const key = e.target.value as string;
                  const p = providers.find((p) => `${p.host}:${p.port}:${p.version}:${p.group}` === key);
                  if (p) handleSelectProvider(p);
                }}
              >
                <Space direction="vertical">
                  {providers.map((p) => {
                    const key = `${p.host}:${p.port}:${p.version}:${p.group}`;
                    return (
                      <Radio key={key} value={key}>
                        <Space>
                          <Tag color="blue">{p.host}:{p.port}</Tag>
                          {p.version && <Tag color="green">v{p.version}</Tag>}
                          {p.group && <Tag color="orange">{p.group}</Tag>}
                        </Space>
                      </Radio>
                    );
                  })}
                </Space>
              </Radio.Group>
            </div>
          )}

          {!queryLoading && providersData && providers.length === 0 && (
            <div className="text-gray-400 text-sm">
              未找到健康实例：请确认目标服务已在当前 api-server 所在环境对应的 Nacos 命名空间下注册新接口
              （不同命名空间如 test / test6 / stag 相互隔离；服务已启动但尚未发布新接口版本时也会显示未找到）
            </div>
          )}

          <Divider plain style={{ margin: '8px 0' }}>连接信息（可手动修改）</Divider>
          <Space wrap>
            <Input addonBefore="Host" value={host} onChange={(e) => setHost(e.target.value)} placeholder="192.168.0.45" style={{ width: 280 }} />
            <InputNumber addonBefore="Port" value={port} min={1} max={65535} onChange={(v) => setPort(Number(v) || DEFAULT_PORT)} style={{ width: 160 }} />
          </Space>
        </Space>
      </Card>

      <Card title="② 反射调用" size="small" extra={<Button size="small" onClick={fillJobTemplate}>填充扣罚周任务模板</Button>}>
        <Space direction="vertical" className="w-full">
          <Input
            addonBefore="类路径"
            value={className}
            onChange={(e) => setClassName(e.target.value)}
            placeholder="swiftx.delivery.job.PenaltySettlementWeeklyJob"
          />
          <Input
            addonBefore="方法名"
            value={methodName}
            onChange={(e) => setMethodName(e.target.value)}
            placeholder="process"
          />

          <Divider plain style={{ margin: '8px 0' }}>入参列表（无参数时删除所有行或留空）</Divider>

          {args.map((a, i) => (
            <div key={i} style={{ width: '100%' }}>
              <Typography.Text strong style={{ fontSize: 15 }}>参数{i + 1}</Typography.Text>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', width: '100%', marginTop: 4 }}>
                <MinusCircleOutlined className="text-red-400 cursor-pointer mt-2" style={{ flex: '0 0 auto' }} onClick={() => removeArg(i)} />
                <AutoComplete
                  value={a.type}
                  options={TYPE_OPTIONS}
                  onChange={(v) => updateArg(i, 'type', v)}
                  filterOption={(input, option) =>
                    (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  style={{ width: 360, flex: '0 0 360px' }}
                >
                  <Input addonBefore="类型" placeholder="com.alibaba.schedulerx.worker.domain.JobContext" />
                </AutoComplete>
                <Input.TextArea
                  value={a.value}
                  onChange={(e) => updateArg(i, 'value', e.target.value)}
                  placeholder='值：JobContext 传 jobParameters（如 {"startEpochMs":1,"endEpochMs":2}，留空=空参数）；String 传 "abc"；数字传 1'
                  rows={4}
                  style={{ flex: 1, minWidth: 0, fontFamily: 'monospace', resize: 'vertical' }}
                />
              </div>
            </div>
          ))}

          <Button type="dashed" icon={<PlusOutlined />} onClick={addArg} style={{ width: 180 }}>添加参数</Button>

          <Button
            type="primary"
            loading={invokeLoading}
            disabled={!host.trim() || !className.trim() || !methodName.trim()}
            onClick={handleInvoke}
          >
            调用
          </Button>
        </Space>
      </Card>

      {result !== null && (
        <Card title="调用结果" size="small">
          <Typography.Paragraph
            copyable
            style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12, marginBottom: 0, maxHeight: 500, overflowY: 'auto' }}
          >
            {result}
          </Typography.Paragraph>
        </Card>
      )}
    </div>
  );
};

export default ReflectInvokePage;
